import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Heart, ChevronLeft, Utensils } from "lucide-react";

const PROTEIN_COLOR = {
  Beef: "bg-red-600",
  Eggs: "bg-amber-500",
  Fish: "bg-sky-600",
  Chicken: "bg-orange-500",
};

const HAND_PLATE = [
  ["Protein", "1–2 palms"],
  ["Carbs", "1 cupped hand"],
  ["Vegetables", "≈ 2 fists"],
  ["Fats", "1 thumb"],
];

export default function EatMealDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/eat/meals/${id}`);
        setM(r.data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
        if (e.response?.status === 404) nav("/app/eat/meals", { replace: true });
      } finally { setLoading(false); }
    })();
  }, [id, nav]);

  const toggleFav = async () => {
    try {
      const r = await api.post(`/eat/favourites/${m.id}`);
      setM({ ...m, is_favourite: r.data.is_favourite });
      toast.success(r.data.is_favourite ? "Saved to favourites" : "Removed from favourites");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading) return <div className="text-sm text-muted-foreground">Loading meal…</div>;
  if (!m) return null;

  return (
    <div className="space-y-8" data-testid={`meal-detail-${m.slug}`}>
      <div>
        <Link to="/app/eat/meals" className="text-xs uppercase tracking-widest text-muted-foreground inline-flex items-center gap-1 hover:text-primary" data-testid="meal-back">
          <ChevronLeft size={14} /> Meal Library
        </Link>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-6">
          <div className="aspect-[16/9] rounded-lg overflow-hidden bg-muted border border-border relative">
            {m.image && <img src={m.image} alt={m.name} className="absolute inset-0 w-full h-full object-cover" />}
            <div className="absolute top-4 left-4">
              <span className={`${PROTEIN_COLOR[m.primary_protein] || "bg-foreground"} text-white text-xs font-head uppercase tracking-widest px-3 py-1.5 rounded-md`}>{m.primary_protein}</span>
            </div>
          </div>
        </div>
        <div className="lg:col-span-6 flex flex-col">
          <div className="label-eyebrow">{m.meal_occasion}</div>
          <h1 className="wordmark mt-2 text-4xl md:text-5xl uppercase leading-[0.95]">{m.name}</h1>
          <div className="mt-4 flex flex-wrap gap-2">
            {(m.tags || []).map((t) => (
              <span key={t} className="text-[10px] uppercase tracking-widest bg-muted text-foreground/80 px-2.5 py-1 rounded-md" data-testid={`meal-tag-${t.toLowerCase().replace(/[^a-z]/g, "")}`}>{t}</span>
            ))}
            <span className="text-[10px] uppercase tracking-widest border border-primary/40 text-primary px-2.5 py-1 rounded-md">{m.carb_profile} Carbs</span>
          </div>
          <div className="mt-5 rounded-md bg-foreground text-background px-4 py-3">
            <div className="text-[10px] uppercase tracking-widest text-background/70">Meal Profile</div>
            <div className="mt-1 font-head uppercase text-sm leading-tight" data-testid="meal-profile">{m.meal_profile}</div>
          </div>
          <button onClick={toggleFav} className={`mt-6 inline-flex items-center gap-2 rounded-md px-4 py-2.5 text-xs font-head uppercase tracking-widest border transition-colors self-start ${m.is_favourite ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:border-primary hover:text-primary"}`} data-testid="meal-detail-fav">
            <Heart size={14} strokeWidth={2.5} fill={m.is_favourite ? "currentColor" : "none"} />
            {m.is_favourite ? "Saved" : "Save to favourites"}
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="label-eyebrow">Ingredients</div>
          <ul className="mt-4 space-y-2" data-testid="meal-ingredients">
            {m.ingredients.map((it) => (
              <li key={it} className="flex gap-2 text-sm leading-relaxed">
                <span className="text-primary shrink-0">·</span><span>{it}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="label-eyebrow">Build It</div>
          <p className="mt-4 text-sm leading-relaxed" data-testid="meal-build-it">{m.build_it}</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-foreground text-background rounded-lg p-6">
          <div className="label-eyebrow !text-background/70">Why It Works</div>
          <p className="mt-4 text-sm leading-relaxed text-background/90">{m.why_it_works}</p>
        </div>
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="label-eyebrow">Family / Easy Swap</div>
          <p className="mt-4 text-sm leading-relaxed">{m.family_swap}</p>
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-3">
          <Utensils className="text-primary" size={20} strokeWidth={2.25} />
          <div className="font-head uppercase text-lg">Hand + Plate Reminder</div>
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          {HAND_PLATE.map(([label, portion]) => (
            <div key={label} className="rounded-md border border-border p-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
              <div className="mt-1 font-head uppercase text-sm">{portion}</div>
            </div>
          ))}
        </div>
        <p className="mt-4 text-xs text-muted-foreground">Portion needs vary. Use as a starting guide, not a medical prescription.</p>
      </div>

      {(m.related || []).length > 0 && (
        <div>
          <div className="label-eyebrow">Related Meals</div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            {m.related.map((r) => (
              <Link key={r.id} to={`/app/eat/meals/${r.slug}`} className="bg-card border border-border rounded-lg overflow-hidden hover:border-primary transition-colors">
                <div className="aspect-[4/3] bg-muted relative">
                  {r.image && <img src={r.image} alt={r.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />}
                </div>
                <div className="p-4">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{r.meal_occasion}</div>
                  <div className="mt-1 font-head uppercase text-sm">{r.name}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div>
        <Link to="/app/eat/meals" className="btn-secondary" data-testid="meal-back-bottom">← Back to Meal Library</Link>
      </div>
    </div>
  );
}
